import {
  Choice,
  ElectionFlow,
  InterestedProductLine,
  RequiredSubInfoPresenter,
  SubscriberErrorMessageType,
  SubscriberProductLineType,
} from 'types/electionService';
import { SubscriberPresenter } from 'store/modules/elections/types';
import { ProductLineId } from '@insurancemenu/imenu-components-lib/dist/types';
import {
  checkIfFormFieldsArePopulated,
  getProductLineRequiredFields,
  TotalRateCalculationResult,
} from './productLineValidation';
import {
  getChoiceByProductLineId,
  getDependentOnProductLineType,
  getDependentOnProductLineTypeFromInterest,
  getInterestedProductLineByProductLineId,
  getPLineProductType,
} from './productLineElection';

export interface TotalRateCalculationParams {
  /** Subscriber fields used for validation */
  subscriber: SubscriberPresenter;
  /** Which election arrays are read:
   *  - enrollment uses `choice`
   *  - quoting uses `interestedProductLines`
   */
  electionFlow: ElectionFlow;
  requiredSubInfo?: RequiredSubInfoPresenter;
}

interface ProductLineElectionValidationSlice {
  currentProductLineType: SubscriberProductLineType;
  dependentOnProductLineType: SubscriberProductLineType | null;
  productId: string;
}

/**
 * Resolves current and dependent-on product line types for one product line.
 */
const getProductLineElectionValidationSlice = (
  productLineId: ProductLineId,
  electionFlow: ElectionFlow,
  choice: Choice[],
  interestedProductLines: InterestedProductLine[],
  requiredSubInfo?: RequiredSubInfoPresenter,
): ProductLineElectionValidationSlice => {
  switch (electionFlow) {
    case 'enrollment': {
      const currentChoice = getChoiceByProductLineId(choice, productLineId);
      return {
        currentProductLineType: getPLineProductType(currentChoice?.productId || ''),
        dependentOnProductLineType: getDependentOnProductLineType(productLineId, choice, requiredSubInfo),
        productId: currentChoice?.productId || '',
      };
    }
    case 'quoting': {
      const interestLine = getInterestedProductLineByProductLineId(interestedProductLines, productLineId);
      return {
        currentProductLineType: getPLineProductType(interestLine?.value || ''),
        dependentOnProductLineType: getDependentOnProductLineTypeFromInterest(
          productLineId,
          interestedProductLines,
          requiredSubInfo,
        ),
        // Products are not available in the quote flow.
        // An empty string is used to indicate that no product is selected,
        // so product-related required field validation can be skipped.
        productId: '',
      };
    }
    default:
      return { currentProductLineType: 'NOT_SELECTED', dependentOnProductLineType: null, productId: '' };
  }
};

/**
 * Aggregates validation for the subscriber.
 * Checks profile-level and product-line-level required fields (including dependencies).
 * Works for plan-based elections or interest-only product lines.
 */
export const checkTotalRateCalculation = ({
  subscriber: { productLineToCarrier, choice, interestedProductLines, common, employeeClass },
  electionFlow,
  requiredSubInfo,
}: TotalRateCalculationParams): TotalRateCalculationResult => {
  const incompleteProductLineIds: ProductLineId[] = [];
  const productLineIdsMissingSelection: ProductLineId[] = [];

  // Check product line specific required fields
  const productLineRequiredInfo = requiredSubInfo?.productLineRequiredInfo || {};
  Object.keys(productLineRequiredInfo).forEach((productLineIdKey) => {
    const productLineId = productLineIdKey as ProductLineId;
    const carrierId = productLineToCarrier[productLineId] || '';

    const { currentProductLineType, dependentOnProductLineType, productId } = getProductLineElectionValidationSlice(
      productLineId,
      electionFlow,
      choice,
      interestedProductLines,
      requiredSubInfo,
    );

    // Skip validation check for not selected product line
    if (currentProductLineType === 'NOT_SELECTED') {
      return;
    }

    const requiredFields = getProductLineRequiredFields(
      {
        productLineId,
        productLineType: currentProductLineType,
        employeeClass,
        carrierId,
        productId,
      },
      requiredSubInfo,
    );

    const areProductLineLevelFieldsValid = checkIfFormFieldsArePopulated(requiredFields, common);

    // Product line is invalid if product line specific fields are not populated
    if (!areProductLineLevelFieldsValid) {
      incompleteProductLineIds.push(productLineId);
    }

    const dependentOnProductLineId = productLineRequiredInfo[productLineId]?.dependentOn;

    if (
      dependentOnProductLineId &&
      currentProductLineType === 'SELECTED' &&
      dependentOnProductLineType !== 'SELECTED'
    ) {
      productLineIdsMissingSelection.push(dependentOnProductLineId);
    }
  });

  const errorTypes: SubscriberErrorMessageType[] = [
    !checkIfFormFieldsArePopulated(requiredSubInfo?.subscriberRequiredFields || [], common) && 'INCOMPLETE_PROFILE',
    incompleteProductLineIds.length > 0 && 'MISSING_PRODUCT_LINE_ANSWERS',
    productLineIdsMissingSelection.length > 0 && 'MISSING_DEPENDENT_PRODUCT_LINE_SELECTION',
  ].filter(Boolean) as SubscriberErrorMessageType[];

  return {
    errorTypes,
    incompleteProductLineIds,
    productLineIdsMissingSelection,
  };
};

/**
 * Subscriber validation based on required-sub rules used in total rate calculation.
 *
 * `requiredSubInfoPresenter` must be built for the same `electionFlow` via `createRequiredSubInfoPresenter`.
 * `subscriber` always carries both `choice` and `interestedProductLines`;
 * validation uses `choice` when `electionFlow` is `enrollment`, and `interestedProductLines` when `quoting`.
 */
export const isSubscriberValid = (
  requiredSubInfoPresenter: RequiredSubInfoPresenter,
  subscriber: SubscriberPresenter,
  electionFlow: ElectionFlow = 'enrollment',
): boolean => {
  return (
    checkTotalRateCalculation({
      subscriber,
      electionFlow,
      requiredSubInfo: requiredSubInfoPresenter,
    }).errorTypes.length === 0
  );
};
